class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Non-blocking drop-down: close on a click OUTSIDE the cart — but not when the
    // click is the cart icon (which toggles) or an add-to-cart button (so the
    // customer can keep adding items without the cart getting in the way).
    document.addEventListener('click', (e) => {
      if (!this.classList.contains('active')) return;
      if (
        e.target.closest('cart-drawer') ||
        e.target.closest('#cart-icon-bubble') ||
        e.target.closest('.product-form__submit, .quick-add__submit, .sticky-atc__button, [name="add"]')
      )
        return;
      this.close();
    });
    // Escape closes from anywhere (there's no focus-trap on the non-blocking dropdown).
    document.addEventListener('keyup', (e) => {
      if (e.code === 'Escape' && this.classList.contains('active')) this.close();
    });
    // Keep the panel correctly anchored while it's open: re-run on viewport resize, and on
    // page scroll (rAF-throttled) so the panel flows with the header instead of being left
    // stuck at a stale, possibly off-screen position.
    window.addEventListener('resize', () => {
      if (this.classList.contains('active')) this.positionDropdown();
    });
    let scrollRaf = null;
    window.addEventListener(
      'scroll',
      () => {
        if (scrollRaf || !this.classList.contains('active')) return;
        scrollRaf = requestAnimationFrame(() => {
          scrollRaf = null;
          if (this.classList.contains('active')) this.positionDropdown();
        });
      },
      { passive: true }
    );

    // "Clear cart" button in the drawer header — empties the whole cart in one action.
    // Delegated on the cart-drawer element because the header is re-rendered on every
    // add/remove, so a directly-bound listener would be lost.
    this.addEventListener('click', (e) => {
      const clearBtn = e.target.closest('[data-cart-clear]');
      if (!clearBtn) return;
      e.preventDefault();
      this.clearCart(clearBtn);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.classList.contains('active') ? this.close() : this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.classList.contains('active') ? this.close() : this.open(cartLink);
      }
    });
  }

  // Position the panel in the empty gutter to the RIGHT of the (narrow, centered)
  // content column, so it sits BESIDE the products instead of on top of them. The
  // header cart icon sits at the content's right edge, so the gutter is the space to
  // its right. Falls back to near-full-width on small screens.
  positionDropdown() {
    // Position the STABLE panel element (#CartDrawer). It survives every re-render, so
    // these inline styles persist — adding/removing items never moves or re-sizes it.
    const panel = this.querySelector('#CartDrawer');
    if (!panel) return;
    const icon = document.querySelector('#cart-icon-bubble');
    // clientWidth excludes the scrollbar — fixed positioning measures from the content edge.
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const vh = window.innerHeight;
    const edge = 16;
    const gap = 10;

    // ---- width + horizontal placement ----
    let cartW;
    if (vw < 750) {
      // small screens: near full-width panel, no content push
      cartW = vw - edge * 2;
      document.documentElement.style.setProperty('--cart-push', '0px');
    } else {
      // desktop: a comfortable panel width, and reserve exactly that much room on the
      // right (--cart-push) so the CSS shifts the page content left and products never
      // sit under the cart — no matter how narrow the window is.
      cartW = Math.min(384, Math.round(vw * 0.34));
      document.documentElement.style.setProperty('--cart-push', cartW + edge + 24 + 'px');
    }
    panel.style.left = 'auto';
    panel.style.right = edge + 'px';
    panel.style.setProperty('max-width', 'none', 'important');
    panel.style.setProperty('width', Math.round(cartW) + 'px', 'important');

    // ---- vertical placement (scroll- and sticky-header-proof) ----
    // Drop the panel just below the cart icon WHILE the icon is visible in the upper part
    // of the screen AND there's room for the panel beneath it; otherwise (the header — and
    // its icon — has scrolled off the top, is hidden, or sits too low) pin the panel near
    // the top of the viewport. Always CLAMP so the whole panel — top AND bottom, including
    // the checkout footer — stays on screen. This kills the old bug where a scrolled-away
    // icon gave a negative top so the panel sat jammed above the viewport and stayed stuck.
    // Re-run on scroll/resize so it flows with the header.
    // minH never demands more height than the viewport actually has (short/landscape screens).
    const minH = Math.min(240, Math.max(120, vh - edge * 2));
    let top = edge;
    if (icon) {
      const r = icon.getBoundingClientRect();
      // only anchor below the icon if it's visible up top AND leaves room for the panel —
      // otherwise pin to the top edge rather than a spot that overlaps the icon or overflows.
      if (r.bottom > 0 && r.bottom < vh * 0.6 && r.bottom + gap + minH <= vh - edge) {
        top = r.bottom + gap;
      }
    }
    top = Math.min(Math.max(edge, top), Math.max(edge, vh - minH - edge));
    // fill from `top` down to the bottom safe area; guarantee top + maxH <= vh - edge so the
    // footer/checkout is never clipped. The empty cart stays compact (short content anyway).
    let maxH = Math.max(minH, vh - top - edge);
    if (this.classList.contains('is-empty')) {
      maxH = Math.min(maxH, Math.round(Math.min(vh * 0.6, 400)));
    }
    panel.style.top = Math.round(top) + 'px';
    panel.style.setProperty('max-height', Math.round(maxH) + 'px', 'important');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.positionDropdown();
    // Add the active class on the next frame so the inline position writes above are
    // committed first — the opacity/transform transition on the stable #CartDrawer then
    // always runs cleanly. (Re-calling open() while already active is a harmless no-op:
    // the class is already present, so nothing re-animates.)
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
      document.body.classList.add('cart-dropdown-open'); // pushes the page content left (CSS)
    });
    // Non-blocking drop-down: intentionally NO body scroll-lock and NO hard focus
    // trap, so the customer can keep browsing and adding without being interrupted.
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('cart-dropdown-open');
    removeTrapFocus(this.activeElement);
  }

  // Empty the entire cart. Uses a two-tap confirm so a stray tap can't wipe the cart:
  // the first tap arms the button ("Tap again to clear") for 3s; a second tap within that
  // window actually clears. Then it re-renders the drawer + header count from the server.
  clearCart(btn) {
    if (this._clearing) return;

    if (btn && btn.dataset.armed !== '1') {
      btn.dataset.armed = '1';
      btn.dataset.label = btn.textContent.trim();
      btn.textContent = btn.getAttribute('data-confirm-label') || 'Tap again to clear';
      btn.classList.add('is-armed');
      clearTimeout(this._clearTimer);
      this._clearTimer = setTimeout(() => {
        if (!btn.isConnected) return;
        btn.dataset.armed = '0';
        btn.textContent = btn.dataset.label || 'Clear cart';
        btn.classList.remove('is-armed');
      }, 3000);
      return;
    }

    clearTimeout(this._clearTimer);
    this._clearing = true;
    if (btn) {
      btn.setAttribute('disabled', '');
      btn.classList.remove('is-armed');
    }

    const clearReq = () =>
      fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } }).then((r) => {
        if (!r.ok) throw new Error('clear ' + r.status);
        return r;
      });

    clearReq()
      // A slow add-to-cart could land AFTER our clear and repopulate the server cart. Verify
      // it's actually empty and clear once more if not, so a stray item can't reach checkout.
      .then(() => fetch('/cart.js').then((r) => r.json()))
      .then((cart) => (cart && cart.item_count > 0 ? clearReq() : null))
      .then(() => fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`))
      .then((response) => {
        if (!response.ok) throw new Error('sections ' + response.status);
        return response.json();
      })
      .then((data) => {
        if (data['cart-drawer']) {
          const parsed = new DOMParser()
            .parseFromString(data['cart-drawer'], 'text/html')
            .querySelector('#CartDrawer');
          if (parsed) document.querySelector('#CartDrawer').innerHTML = parsed.innerHTML;
        }
        if (data['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          const parsed = new DOMParser()
            .parseFromString(data['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section');
          if (bubble && parsed) bubble.innerHTML = parsed.innerHTML;
        }
        this.classList.add('is-empty');
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.positionDropdown();
        // The Clear button (which held keyboard focus) was just removed with the re-render —
        // re-home focus inside the drawer instead of letting it fall to <body>.
        if (this.classList.contains('active')) {
          const refocus = this.querySelector(
            '.drawer__inner-empty .button, .drawer__inner-empty .drawer__close, .drawer__inner .drawer__close'
          );
          if (refocus) {
            try {
              refocus.focus({ preventScroll: true });
            } catch (e) {
              refocus.focus();
            }
          }
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-clear', cartData: { item_count: 0 } });
        }
      })
      .catch(() => {
        // Network / bad response: re-enable + reset the button so the customer can retry,
        // rather than leaving a dead, greyed-out control.
        if (btn && btn.isConnected) {
          btn.removeAttribute('disabled');
          btn.dataset.armed = '0';
          btn.textContent = btn.dataset.label || 'Clear cart';
          btn.classList.remove('is-armed');
        }
      })
      .finally(() => {
        this._clearing = false;
      });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
